import { defineStore } from 'pinia';
import { ref } from 'vue';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from './auth';
import { mapLocationFromDb, mapLocationToDb } from '../utils/mappers';
import { fetchAllPages } from '../utils/supabasePagination';

export const useLocationStore = defineStore('locations', () => {
  const locations = ref([]);
  const loading = ref(false);
  const error = ref(null);
  
  // Cache control - prevent redundant fetches within a short time window
  let lastFetchTime = null;
  let fetchInProgress = false;
  const CACHE_DURATION_MS = 30000; // 30 seconds

  async function fetchLocations(forceRefresh = false) {
    // Prevent duplicate calls while already fetching
    if (fetchInProgress) {
      return;
    }
    
    // Check cache - skip fetch if data is fresh (unless forced)
    if (!forceRefresh && lastFetchTime && locations.value.length > 0) {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      if (timeSinceLastFetch < CACHE_DURATION_MS) {
        return;
      }
    }
    
    fetchInProgress = true;
    
    try {
      loading.value = true;
      error.value = null;
      
      // RLS handles organization filtering
      const { data, error: err } = await fetchAllPages((from, to) =>
        supabase
          .from('locations')
          .select(`
            *,
            offices (
              id,
              name
            )
          `)
          .eq('_deleted', false)
          .order('name', { ascending: true })
          .range(from, to)
      );

      if (err) throw err;

      locations.value = (data || []).map(loc => ({
        ...mapLocationFromDb(loc),
        officeName: loc.offices?.name || null
      }));
      lastFetchTime = Date.now();
    } catch (err) {
      error.value = 'Failed to fetch locations';
      console.error('Error:', err);
    } finally {
      loading.value = false;
      fetchInProgress = false;
    }
  }
  
  // Force refresh from server (invalidates cache)
  async function refreshLocations() {
    return fetchLocations(true);
  }

  async function createLocation(location) {
    try {
      loading.value = true;
      error.value = null;
      
      const authStore = useAuthStore();
      if (!authStore.organizationId) throw new Error('No organization selected');

      const { data, error: err } = await supabase
        .from('locations')
        .insert([mapLocationToDb(location, authStore.organizationId, authStore.user?.id)])
        .select()
        .single();

      if (err) throw err;

      locations.value.push(mapLocationFromDb(data));

      return data;
    } catch (err) {
      error.value = 'Failed to create location';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateLocation(location) {
    try {
      loading.value = true;
      error.value = null;

      const authStore = useAuthStore();
      const updateData = mapLocationToDb(location, authStore.organizationId);
      delete updateData.organization_id; // Don't update organization_id

      const { error: err } = await supabase
        .from('locations')
        .update(updateData)
        .eq('id', location.id);

      if (err) throw err;

      const index = locations.value.findIndex(l => l.id === location.id);
      if (index !== -1) {
        locations.value[index] = location;
      }
    } catch (err) {
      error.value = 'Failed to update location';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteLocation(id) {
    try {
      loading.value = true;
      error.value = null;

      // Soft delete using RPC to bypass potential RLS issues
      const { error: err } = await supabase
        .rpc('soft_delete_location', { p_location_id: id });

      if (err) throw err;

      locations.value = locations.value.filter(location => location.id !== id);
    } catch (err) {
      error.value = 'Failed to delete location';
      console.error('Error:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    locations,
    loading,
    error,
    fetchLocations,
    refreshLocations,
    createLocation,
    updateLocation,
    deleteLocation
  };
});
